import { Messages } from '../../../codegen/Localize';
import { CommonErrorCode, UserErrorCode } from '../../../ts/helpers/ErrorCodes';
import { UserActionNotificationSuccessCode } from '../../../ts/helpers/SuccessCodes';
import { L10n } from '../../../ts/L10n/L10n';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { Set } from '../../../ts/models/Set';
import { IUpdateServerUsersSiteMembershipResultCodes } from '../../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { ISiteUserActionError } from '../../../ts/ServerApi/types/IUserActionResult';
import { LicenseTierFilter } from '../filters/LicenseTierFilter';
import { filter } from '../filters/SiteRoleFilter';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { ISiteRoleChange } from '../SiteRolesPickerClientService';
import { DetailType, ISiteMembershipDetail, MultipleSiteRoleMembershipActionNotification } from './MultipleSiteRoleMembershipActionNotification';

// Notification For:
// => site membership and roles change for single site

export class SiteMembershipUpdateActionNotification {
  private siteRolesOptionsResource: SiteRoleOptionsResource;

  public static readonly instance = new SiteMembershipUpdateActionNotification();

  public notify(
      results: IUpdateServerUsersSiteMembershipResultCodes[],
      errors: ISiteUserActionError[],
      users: Set<IUserInfo>,
      siteRoleChanges: _.Dictionary<ISiteRoleChange>): void {

    this.siteRolesOptionsResource = SiteRoleOptionsResource.instance;

    const {siteSuccessDetails, siteErrorDetails} = this.setupSiteMembershipResults(results, errors, users, siteRoleChanges);

    if(siteSuccessDetails.length === 1){
      MultipleSiteRoleMembershipActionNotification.notifySuccessSummary(this.getSingleMessage(siteSuccessDetails[0]));
    } else{
      MultipleSiteRoleMembershipActionNotification.notifySuccessResult(siteSuccessDetails);
    }

    if(siteErrorDetails.length === 1){
      MultipleSiteRoleMembershipActionNotification.notifyErrorSummary(this.getSingleMessage(siteErrorDetails[0]));
    } else {
      MultipleSiteRoleMembershipActionNotification.notifyErrorResult(siteErrorDetails);
    }
  }

  private setupSiteMembershipResults(results: IUpdateServerUsersSiteMembershipResultCodes[], errors: ISiteUserActionError[], usersMap: Set<IUserInfo>, siteRoleChanges: _.Dictionary<ISiteRoleChange>){
    const siteSuccessDetails: ISiteMembershipDetail[] = [];
    const siteErrorDetails: ISiteMembershipDetail[] = [];

    for(const result of results){
      const user = usersMap.get(result.id);

      //get sucess and error messages mapped to more than one result codes
      this.getSuccessOrErrorDetailsMappedToMultipleResultCodes(result.codes, siteSuccessDetails , siteErrorDetails, user, siteRoleChanges[result.siteId].siteRole, siteRoleChanges[result.siteId].siteName);

      //compute the remaining result codes
      for(const code of result.codes){
        const siteRole = siteRoleChanges[result.siteId].siteRole;
        const siteName = siteRoleChanges[result.siteId].siteName;
        const detailItem = this.createSuccessOrErrorDetails(user, code, siteRole, siteName);
        if (detailItem && detailItem.detailType === DetailType.Success) {
          siteSuccessDetails.push(detailItem);
        } else if (detailItem && detailItem.detailType === DetailType.Error) {
          siteErrorDetails.push(detailItem);
        }
      }
    }

    for(const error of errors ? errors : []){
      const user = usersMap.get(error.id);

      //compute the remaining result codes
      const siteRole = error.siteId ? siteRoleChanges[error.siteId].siteRole : '';
      const siteName = error.siteId ? siteRoleChanges[error.siteId].siteName : '';
      const detailItem = this.createSuccessOrErrorDetails(user, error.code, siteRole, siteName);
      if (detailItem && detailItem.detailType === DetailType.Error) {
        siteErrorDetails.push(detailItem);
      }
    }

    return {siteSuccessDetails, siteErrorDetails};
  }

  private getSuccessOrErrorDetailsMappedToMultipleResultCodes(codes: number[], siteSuccessDetails: ISiteMembershipDetail[], siteErrorDetails: ISiteMembershipDetail[], user: IUserInfo, siteRole: string, siteName: string){

    const userAdded = codes.indexOf(UserActionNotificationSuccessCode.UserAdded) > -1;
    const userAssigned = codes.indexOf(UserActionNotificationSuccessCode.UserAssigned) > -1;
    const serverInsufficientLicenses = codes.indexOf(UserErrorCode.ServerUserInsufficientLicenses) > -1;
    if((userAdded && userAssigned) || (userAdded && serverInsufficientLicenses)){
      if(userAssigned){
        codes.splice(codes.indexOf(UserActionNotificationSuccessCode.UserAssigned), 1);
        siteSuccessDetails.push(this.createSuccessOrErrorDetails(user,UserActionNotificationSuccessCode.UserAssigned, siteRole, siteName, true));
      } else {
        codes.splice(codes.indexOf(UserErrorCode.ServerUserInsufficientLicenses), 1);
        siteErrorDetails.push(this.createSuccessOrErrorDetails(user,UserErrorCode.ServerUserInsufficientLicenses, siteRole, siteName, true));
      }
      codes.splice(codes.indexOf(UserActionNotificationSuccessCode.UserAdded), 1);
    }
  }

  private createSuccessOrErrorDetails(user: IUserInfo, code: number, siteRole: string, siteName: string, isUserAdded = false): ISiteMembershipDetail {
    const { detailType, message } = this.getSuccessOrErrorStatusWithMessage(code, user ? user.username : undefined, siteRole, isUserAdded);
    if (detailType == null) {
      return null;
    }

    return {
      detailType: detailType,
      userName: user.username,
      siteName: siteName,
      message: message,
      userInfo: user,
      siteRole: siteRole,
      code: code,
      isUserAdded: isUserAdded
    };
  }

  private getSuccessOrErrorStatusWithMessage(code: number, username: string, siteRole: string, isUserAdded: boolean) {
    const translatedRole = filter(siteRole);
    if(!isUserAdded){
      switch (code) {
        case UserActionNotificationSuccessCode.UserRemoved:
          return {detailType: DetailType.Success, message: Messages.singleUserRemoved()};
        case UserActionNotificationSuccessCode.UserAdded:
          return {detailType: DetailType.Success, message: Messages.singleUserAdded()};
        case UserActionNotificationSuccessCode.UserAssigned:
          return {detailType: DetailType.Success, message: Messages.userAssignedTo({SITE_ROLE: translatedRole})};
        case CommonErrorCode.SystemUserNotFound:
          return {detailType: DetailType.Error, message: Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(username) })};
        case UserErrorCode.ServerUserInsufficientLicenses:
          const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole).licenseTierRole;
          const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);
          return {detailType: DetailType.Error, message: Messages.errorUserInsufficientLicenses_updateSiteRole({SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole })};
        case UserErrorCode.UserCannotHaveSiteRoleLowerThanHighestMinimumLulGroupRole:
          return {detailType: DetailType.Error, message: Messages.errorUserCannotHaveSiteRoleLowerThan_highestMinimumLulGroupRole({SITE_ROLE: translatedRole})};
        case UserErrorCode.UserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin:
          return {detailType: DetailType.Error, message: Messages.errorUserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin({USER_NAME: L10n.elideIfLong(username)})};
        case UserErrorCode.UserNotSupportedGuestOperation:
          return {detailType: DetailType.Error, message: Messages.errorUserNotSupportedGuestOperation()};
        case UserErrorCode.ServerUserInsufficientSiteQuota:
          return {detailType: DetailType.Error, message: Messages.errorUserInsufficientSiteQuota()};
        case UserErrorCode.UserAlreadyExists:
          return {detailType: DetailType.Error, message: Messages.userNameExistsOnServerForAddUserToSiteSAML({USER_NAME: L10n.elideIfLong(username)})};
        case UserErrorCode.UserCannotChangeFromSupportUser:
          return {detailType: DetailType.Error, message: Messages.errorUserCannotChangeFromSupportUser()};
        case UserErrorCode.UserNotDeleted:
          return {detailType: DetailType.Error, message: Messages.userNotRemoved()};
        case UserErrorCode.UserUnlicensed:
          return {detailType: DetailType.Error, message: Messages.userNotRemoved()};
        default:
          return {detailType: null, message: null};
      }
    } else {
      switch(code){
      case UserActionNotificationSuccessCode.UserAssigned:
        return {detailType: DetailType.Success, message: Messages.userAddedAndAssignedTo({SITE_ROLE: translatedRole})};
      case UserErrorCode.ServerUserInsufficientLicenses:
        const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole).licenseTierRole;
        const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);
        return {detailType: DetailType.Error, message: Messages.errorUserInsufficientLicenses_OnAddUser({SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole })};
      default:
        return {detailType: null, message: null};
      }
    }
  }

  private getSingleMessage(detail: ISiteMembershipDetail){
    const translatedRole = filter(detail.siteRole);
    const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(detail.siteRole).licenseTierRole;
    const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);
    if(!detail.isUserAdded){
      switch (detail.code) {
        case UserActionNotificationSuccessCode.UserRemoved:
          return Messages.userRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserActionNotificationSuccessCode.UserAdded:
          return Messages.singleUserAddedToSite({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserActionNotificationSuccessCode.UserAssigned:
          return Messages.singleUserAssignedTo({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, SITE_NAME: detail.siteName});
        case CommonErrorCode.SystemUserNotFound:
          return Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(detail.userName) });
        case UserErrorCode.ServerUserInsufficientLicenses:
          return Messages.errorUserInsufficientLicenses_singleUserSiteRole({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole, SITE_NAME: detail.siteName});
        case UserErrorCode.UserCannotHaveSiteRoleLowerThanHighestMinimumLulGroupRole:
          return Messages.errorUserCannotHaveSiteRoleLowerThan_highestMinimumLulGroupRole_with_username({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole});
        case UserErrorCode.UserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin:
          return Messages.errorUserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin_with_sitename({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserErrorCode.UserNotSupportedGuestOperation:
          return Messages.errorUserNotSupportedGuestOperation();
        case UserErrorCode.ServerUserInsufficientSiteQuota:
          return Messages.errorUserInsufficientSiteQuota_singleUserSiteRole({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, SITE_NAME: detail.siteName});
        case UserErrorCode.UserAlreadyExists:
          return Messages.userNameExistsOnServerForAddUserToSiteSAML({USER_NAME: L10n.elideIfLong(detail.userName)});
        case UserErrorCode.UserCannotChangeFromSupportUser:
          return Messages.errorUserCannotChangeFromSupportUser();
        case UserErrorCode.UserNotDeleted:
          return Messages.userNotRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        case UserErrorCode.UserUnlicensed:
          return Messages.userNotRemovedFrom({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName});
        default:
          return Messages.errorUnknown();
      }
    } else { // user added
        switch(detail.code){
          case UserActionNotificationSuccessCode.UserAssigned:
            return Messages.singleUserAddedAndAssignedTo({USER_NAME: L10n.elideIfLong(detail.userName), SITE_NAME: detail.siteName, SITE_ROLE: translatedRole});
          case UserErrorCode.ServerUserInsufficientLicenses:
            return Messages.errorUserInsufficientLicenses_singleUserSiteRoleOnAddUser({USER_NAME: L10n.elideIfLong(detail.userName), SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole,  SITE_NAME: detail.siteName});
          default:
            return Messages.errorUnknown();
        }
    }
  }
}

export default SiteMembershipUpdateActionNotification.instance;
